/**
 * Premium Product Page — ES module
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney as formatThemeMoney } from '@theme/money-formatting';
import { applyCartMinimum, isCartEligible } from '@theme/cart-minimum-order';

/** @param {number} cents */
function formatMoney(cents) {
  const value = Number(cents);
  if (!Number.isFinite(value)) return '';

  const format = window.Theme?.moneyFormat;
  const currency = window.Theme?.currency || 'INR';

  if (format) {
    return formatThemeMoney(value, format, currency);
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

/** @param {number} count */
function barItemLabel(count) {
  return count === 1 ? 'Item' : 'Items';
}

/** @param {number} count */
function itemLabel(count) {
  return count === 1 ? 'item in cart' : 'items in cart';
}

/** @param {string} str */
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const MOBILE_CART_MQ = window.matchMedia('(max-width: 749px)');

function useThemeCartDrawerOnMobile() {
  return MOBILE_CART_MQ.matches;
}

function openThemeCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
}

/** @param {HTMLFormElement | FormData} formOrData @param {number} [quantityOverride] @param {{ silentAdd?: boolean }} [options] */
async function addProductToCart(formOrData, quantityOverride, options = {}) {
  const formData =
    formOrData instanceof FormData ? new FormData(formOrData) : new FormData(formOrData);

  if (quantityOverride !== undefined) {
    formData.set('quantity', String(Math.max(1, quantityOverride)));
  }
  const sectionIds = [];

  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      sectionIds.push(item.dataset.sectionId);
    }
  });

  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
  }

  const itemCount = Number(formData.get('quantity')) || 1;
  const variantId = formData.get('id');
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: itemCount }],
      promise: deferred.promise,
    })
  );

  const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const res = await fetch(cartAddUrl, {
    method: 'POST',
    headers: {
      Accept: 'text/html',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: formData,
  });
  const data = await res.json();

  if (data.status) {
    deferred.reject(new Error(data.message || data.description || 'Add to cart failed'));
    throw new Error(data.message || data.description || 'Add to cart failed');
  }

  const cartUrl = window.Theme?.routes?.cart_url || '/cart';
  const cartRes = await fetch(`${cartUrl}.json`);
  const ajaxCart = await cartRes.json();

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: {
      items: ajaxCart.items,
      sections: data.sections,
      didError: false,
      source: 'premium-pdp',
      silentAdd: Boolean(options.silentAdd),
      addedQuantity: itemCount,
      cartItemCount: ajaxCart.item_count,
      cartTotal: ajaxCart.total_price,
      variantId: String(variantId),
    },
  });

  return ajaxCart;
}

  /* ─── Gallery ─── */
  class PpdpGallery {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.main = root.querySelector('[data-ppdp-main]');
      this.track = root.querySelector('[data-ppdp-track]');
      this.config = this.parseConfig();
      this.bind();
      this.refreshGalleryNodes();
    }

    parseConfig() {
      try {
        const el = this.root.querySelector('[data-ppdp-gallery-config]');
        return el ? JSON.parse(el.textContent) : { variants: [] };
      } catch {
        return { variants: [] };
      }
    }

    refreshGalleryNodes() {
      this.slides = this.root.querySelectorAll('[data-ppdp-slide]');
      this.thumbs = this.root.querySelectorAll('[data-ppdp-thumb]');
      this.dots = this.root.querySelectorAll('[data-ppdp-dot]');
    }

    /** @returns {HTMLElement[]} */
    getVisibleThumbs() {
      return [...this.thumbs].filter((thumb) => !thumb.hidden);
    }

    /** @param {HTMLElement} el @param {number|string} variantId */
    isMediaVisibleForVariant(el, variantId) {
      const owners = (el.dataset.variantOwners || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

      if (owners.length === 0) return true;
      return owners.includes(String(variantId));
    }

    /** @param {{ featured_media_id?: number | null, media_ids?: number[] }} variant */
    getVariantMediaIds(variant) {
      const ids = new Set((variant.media_ids || []).map(String));
      if (variant.featured_media_id != null) ids.add(String(variant.featured_media_id));
      return ids;
    }

    /** @param {string} mediaId */
    findSlideByMediaId(mediaId) {
      return [...this.slides].find((slide) => slide.dataset.mediaId === String(mediaId) && !slide.hidden);
    }

    /** @param {string} mediaId */
    findThumbByMediaId(mediaId) {
      return [...this.thumbs].find((thumb) => thumb.dataset.mediaId === String(mediaId) && !thumb.hidden);
    }

    /** @param {string} mediaId */
    setActiveMediaId(mediaId) {
      const id = String(mediaId);
      this.thumbs.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === id && !thumb.hidden));
      this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaId === id && !dot.hidden));
    }

    /** @param {string} src */
    resolveSrc(src) {
      if (!src) return '';
      if (src.startsWith('//')) return `${window.location.protocol}${src}`;
      return src;
    }

    /** @param {{ featured_media_id?: number | null, featured_media_src?: string | null, featured_media_type?: string, media_ids?: number[] }} variant */
    updateMainFromVariant(variant) {
      if (!this.main) return;

      const featuredId =
        variant.featured_media_id != null ? String(variant.featured_media_id) : null;

      if (featuredId) {
        const thumb = this.findThumbByMediaId(featuredId);
        if (thumb) {
          this.select(thumb);
          return;
        }

        const slide = this.findSlideByMediaId(featuredId);
        if (slide) {
          this.main.innerHTML = slide.innerHTML;
          this.setActiveMediaId(featuredId);
          if (this.isMobileTrack() && this.track) {
            this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
          }
          return;
        }
      }

      const src = this.resolveSrc(variant.featured_media_src);
      if (!src) return;

      const type = variant.featured_media_type || 'image';
      if (type === 'video') {
        this.main.innerHTML = `<video src="${src}" controls playsinline class="ppdp-gallery__video"></video>`;
      } else {
        this.main.innerHTML = `<img src="${src}" alt="" loading="lazy" width="800" height="800">`;
      }

      if (featuredId) this.setActiveMediaId(featuredId);
    }

    /** @param {{ id?: number, featured_media_id?: number | null, featured_media_src?: string | null, featured_media_type?: string, media_ids?: number[] }} variant */
    applyVariantFilter(variant) {
      if (!variant?.id) return;

      const variantId = String(variant.id);
      const mediaIds = this.getVariantMediaIds(variant);
      const galleryItems = this.root.querySelectorAll('[data-ppdp-slide], [data-ppdp-thumb], [data-ppdp-dot]');

      if (galleryItems.length === 0) {
        this.updateMainFromVariant(variant);
        return;
      }

      galleryItems.forEach((el) => {
        const mediaId = el.dataset.mediaId;
        const ownerVisible = this.isMediaVisibleForVariant(el, variantId);
        const inGallery = mediaId && mediaIds.has(String(mediaId));
        el.hidden = !(ownerVisible || inGallery);
      });

      this.refreshGalleryNodes();

      const featuredId =
        variant.featured_media_id != null ? String(variant.featured_media_id) : null;

      let target = featuredId ? this.findThumbByMediaId(featuredId) : null;
      if (!target) target = [...this.thumbs].find((thumb) => !thumb.hidden);

      if (target) {
        this.select(target);
      } else {
        this.updateMainFromVariant(variant);
      }
    }

    isMobileTrack() {
      return Boolean(this.track) && window.matchMedia('(max-width: 749px)').matches;
    }

    bind() {
      this.scrollRaf = 0;

      this.root.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-ppdp-thumb]');
        if (thumb && this.root.contains(thumb)) {
          this.select(thumb);
          return;
        }

        const dot = event.target.closest('[data-ppdp-dot]');
        if (!dot || dot.hidden || !this.root.contains(dot)) return;

        const mediaId = dot.dataset.mediaId;
        const dotThumb = mediaId ? this.findThumbByMediaId(mediaId) : null;

        if (dotThumb) {
          this.select(dotThumb);
          return;
        }

        if (this.isMobileTrack() && mediaId) {
          const slide = this.findSlideByMediaId(mediaId);
          if (slide && this.track) {
            this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
            this.setActiveMediaId(mediaId);
          }
        }
      });

      this.root.querySelector('[data-ppdp-gallery-prev]')?.addEventListener('click', () => this.navigate(-1));
      this.root.querySelector('[data-ppdp-gallery-next]')?.addEventListener('click', () => this.navigate(1));

      this.root.addEventListener('keydown', (event) => {
        if (!window.matchMedia('(min-width: 750px)').matches || !this.thumbs.length) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1);
        }
      });

      if (this.track) {
        this.track.addEventListener(
          'scroll',
          () => {
            if (!this.isMobileTrack()) return;
            cancelAnimationFrame(this.scrollRaf);
            this.scrollRaf = requestAnimationFrame(() => this.syncTrackIndex());
          },
          { passive: true }
        );
      }

      let startX = 0;
      this.main?.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
        },
        { passive: true }
      );

      this.main?.addEventListener(
        'touchend',
        (e) => {
          if (this.isMobileTrack()) return;
          const diff = startX - e.changedTouches[0].clientX;
          if (Math.abs(diff) < 40) return;
          const visible = this.getVisibleThumbs();
          const active = visible.findIndex((t) => t.classList.contains('is-active'));
          const next = diff > 0 ? active + 1 : active - 1;
          if (visible[next]) this.select(visible[next]);
        },
        { passive: true }
      );

      document.addEventListener('ppdp:variant-change', (event) => {
        this.applyVariantFilter(event.detail?.variant);
      });
    }

    /** @param {number} dir */
    navigate(dir) {
      const visible = this.getVisibleThumbs();
      const active = visible.findIndex((t) => t.classList.contains('is-active'));
      const next = visible[active + dir];
      if (next) this.select(next);
    }

    /** @param {string} mediaId */
    goToSlideByMediaId(mediaId) {
      const slide = this.findSlideByMediaId(mediaId);
      if (!slide || !this.track) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      this.setActiveMediaId(mediaId);
    }

    /** @param {number} index */
    goToSlide(index) {
      const slide = this.slides[index];
      if (!slide || slide.hidden || !this.track) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      if (slide.dataset.mediaId) this.setActiveMediaId(slide.dataset.mediaId);
    }

    syncTrackIndex() {
      if (!this.track || !this.slides.length) return;
      const trackLeft = this.track.scrollLeft;
      let closestSlide = null;
      let closestDist = Infinity;

      this.slides.forEach((slide) => {
        if (slide.hidden) return;
        const dist = Math.abs(slide.offsetLeft - trackLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closestSlide = slide;
        }
      });

      if (closestSlide?.dataset.mediaId) {
        this.setActiveMediaId(closestSlide.dataset.mediaId);
      }
    }

    /** @param {HTMLElement} thumb */
    select(thumb) {
      if (thumb.hidden) return;

      this.refreshGalleryNodes();

      const mediaId = thumb.dataset.mediaId;
      if (!mediaId || !this.main) return;

      this.setActiveMediaId(mediaId);

      const slide = this.findSlideByMediaId(mediaId);
      if (slide) {
        this.main.innerHTML = slide.innerHTML;
        if (this.isMobileTrack() && this.track) {
          this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        }
        return;
      }

      const src = this.resolveSrc(thumb.dataset.fullSrc);
      const type = thumb.dataset.mediaType || 'image';
      if (!src) return;

      if (type === 'video') {
        this.main.innerHTML = `<video src="${src}" controls playsinline class="ppdp-gallery__video"></video>`;
      } else {
        this.main.innerHTML = `<img src="${src}" alt="" loading="lazy" width="800" height="800">`;
      }

      const hiddenSlide = [...this.slides].find((s) => s.dataset.mediaId === mediaId);
      if (this.isMobileTrack() && this.track && hiddenSlide && !hiddenSlide.hidden) {
        this.track.scrollTo({ left: hiddenSlide.offsetLeft, behavior: 'smooth' });
      }
    }
  }

  /* ─── Variants ─── */
  class PpdpVariants {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.form = root.closest('form');
      this.variantInput = this.form?.querySelector('[name="id"]');
      this.data = this.parseData();
      this.bind();
      this.syncSelections();
      this.updateUI(this.getSelectedVariant());
    }

    parseData() {
      try {
        const el = this.root.querySelector('[data-ppdp-variants]');
        return el ? JSON.parse(el.textContent) : { variants: [], options: [] };
      } catch {
        return { variants: [], options: [] };
      }
    }

    bind() {
      this.root.querySelectorAll('[data-ppdp-option-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.hidden || btn.disabled) return;

          const group = btn.closest('[data-ppdp-option]');
          group?.querySelectorAll('[data-ppdp-option-btn]').forEach((b) => {
            b.classList.remove('is-selected');
            b.setAttribute('aria-selected', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-selected', 'true');

          this.syncSelections();
          this.updateUI(this.getSelectedVariant());
        });
      });
    }

    getSelectedOptions() {
      return [...this.root.querySelectorAll('[data-ppdp-option]')].map((group) => {
        const selected = group.querySelector('[data-ppdp-option-btn].is-selected');
        return selected?.dataset.value || '';
      });
    }

    /** @param {string[]} options */
    findVariantForOptions(options) {
      return this.data.variants.find((variant) =>
        variant.options.every((value, index) => value === options[index])
      );
    }

    /**
     * Can this option value exist with the other currently selected options?
     * @param {number} optionIndex
     * @param {string} value
     * @param {string[]} selectedOptions
     */
    optionValueExists(optionIndex, value, selectedOptions) {
      return this.data.variants.some((variant) => {
        if (variant.options[optionIndex] !== value) return false;

        return variant.options.every((optionValue, index) => {
          if (index === optionIndex) return true;
          const selected = selectedOptions[index];
          if (!selected) return true;
          return optionValue === selected;
        });
      });
    }

    applyOptionAvailability() {
      const selectedOptions = this.getSelectedOptions();

      this.root.querySelectorAll('[data-ppdp-option]').forEach((group, optionIndex) => {
        group.querySelectorAll('[data-ppdp-option-btn]').forEach((btn) => {
          const value = btn.dataset.value || '';
          const exists = this.optionValueExists(optionIndex, value, selectedOptions);

          const testOptions = selectedOptions.slice();
          testOptions[optionIndex] = value;
          const variant = this.findVariantForOptions(testOptions);

          btn.hidden = !exists;
          btn.setAttribute('aria-hidden', exists ? 'false' : 'true');

          if (!exists) {
            btn.disabled = true;
            btn.classList.remove('is-selected');
            btn.setAttribute('aria-selected', 'false');
          } else {
            btn.disabled = false;
            if (variant && !variant.available) {
              btn.disabled = true;
            }
          }
        });
      });
    }

    syncSelections() {
      const maxIterations = Math.max(2, (this.data.options?.length || 0) + 1);

      for (let i = 0; i < maxIterations; i += 1) {
        this.applyOptionAvailability();

        let changed = false;

        this.root.querySelectorAll('[data-ppdp-option]').forEach((group) => {
          const visibleButtons = [...group.querySelectorAll('[data-ppdp-option-btn]')].filter(
            (button) => !button.hidden
          );
          const selectedVisible = group.querySelector(
            '[data-ppdp-option-btn].is-selected:not([hidden])'
          );

          if (!selectedVisible && visibleButtons.length > 0) {
            group.querySelectorAll('[data-ppdp-option-btn]').forEach((button) => {
              button.classList.remove('is-selected');
              button.setAttribute('aria-selected', 'false');
            });
            visibleButtons[0].classList.add('is-selected');
            visibleButtons[0].setAttribute('aria-selected', 'true');
            changed = true;
          }
        });

        if (!changed) break;
      }
    }

    getSelectedVariant() {
      const opts = this.getSelectedOptions();
      return (
        this.findVariantForOptions(opts) ||
        this.data.variants.find((v) => v.available) ||
        this.data.variants[0]
      );
    }

    /** @param {object} variant */
    updateUI(variant) {
      if (!variant) return;
      if (this.variantInput) this.variantInput.value = variant.id;

      const priceEl = this.root.querySelector('[data-ppdp-price-current]');
      const compareEl = this.root.querySelector('[data-ppdp-price-compare]');
      const discountEl = this.root.querySelector('[data-ppdp-price-discount]');
      const unitEl = this.root.querySelector('[data-ppdp-price-unit]');
      const atcBtn = this.form?.querySelector('[data-ppdp-atc]');
      const stockEl = this.root.querySelector('[data-ppdp-stock]');

      if (priceEl) priceEl.textContent = variant.price_formatted;
      if (compareEl) {
        compareEl.textContent = variant.compare_at_price_formatted || '';
        compareEl.hidden = !variant.compare_at_price;
      }
      if (discountEl) {
        discountEl.textContent = variant.discount ? `${variant.discount}% OFF` : '';
        discountEl.hidden = !variant.discount;
      }
      if (unitEl) {
        const unitCount = Number(variant.unit_count);
        const unitLabel = String(variant.unit_label || '').trim();
        if (unitCount > 0 && unitLabel && Number.isFinite(Number(variant.price)) && variant.price > 0) {
          const unitCents = Math.round(Number(variant.price) / unitCount);
          unitEl.textContent = `${formatMoney(unitCents)} / ${unitLabel}`;
          unitEl.hidden = false;
        } else {
          unitEl.textContent = '';
          unitEl.hidden = true;
        }
      }
      if (atcBtn) {
        const launchingSoon = this.root.dataset.ppdpLaunchingSoon === 'true';
        const labelLaunching = this.root.dataset.ppdpLabelLaunching || atcBtn.dataset.labelLaunching || 'Launching Soon';

        if (launchingSoon) {
          atcBtn.disabled = true;
          atcBtn.textContent = labelLaunching;
        } else {
          atcBtn.disabled = !variant.available;
          atcBtn.textContent = variant.available
            ? atcBtn.dataset.labelAdd || 'Add to Cart'
            : atcBtn.dataset.labelSold || 'Sold Out';
        }
      }
      if (stockEl) {
        const launchingSoon = this.root.dataset.ppdpLaunchingSoon === 'true';
        if (launchingSoon) {
          stockEl.textContent = stockEl.dataset.launchingSoon || 'Coming soon — not available to order yet';
          stockEl.classList.remove('ppdp-stock--in', 'ppdp-stock--out');
          stockEl.classList.add('ppdp-stock--soon');
        } else {
          stockEl.textContent = variant.available ? stockEl.dataset.inStock || 'In stock' : stockEl.dataset.outStock || 'Out of stock';
          stockEl.classList.toggle('ppdp-stock--in', variant.available);
          stockEl.classList.toggle('ppdp-stock--out', !variant.available);
          stockEl.classList.remove('ppdp-stock--soon');
        }
      }

      document.dispatchEvent(new CustomEvent('ppdp:variant-change', { detail: { variant } }));
    }
  }

  /* ─── Product form ─── */
  class PpdpForm {
    /** @param {HTMLFormElement} form */
    constructor(form) {
      this.form = form;
      form.addEventListener('submit', (e) => this.submit(e));
    }

    async submit(e) {
      e.preventDefault();
      const root = this.form.closest('[data-ppdp-variants-root]');
      if (root?.dataset.ppdpLaunchingSoon === 'true') return;

      const btn = this.form.querySelector('[data-ppdp-atc]');
      const wasDisabled = btn?.disabled;
      if (btn) btn.disabled = true;

      try {
        await addProductToCart(this.form, undefined, { silentAdd: true });
      } catch (err) {
        console.error(err);
      } finally {
        if (btn && !wasDisabled && root?.dataset.ppdpLaunchingSoon !== 'true') btn.disabled = false;
      }
    }
  }

  /* ─── Buy now ─── */
  function bindBuyNow() {
    document.querySelectorAll('[data-ppdp-buy-now]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const form = btn.closest('form');
        if (!form) return;
        const root = form.closest('[data-ppdp-variants-root]');
        if (root?.dataset.ppdpLaunchingSoon === 'true') return;
        try {
          await addProductToCart(form);
          const cart = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`).then((r) => r.json());
          applyCartMinimum(cart);

          if (!isCartEligible(cart)) {
            document.querySelector('[data-ppdp-cart-bar-open]')?.click();
            return;
          }

          window.location.href = window.Theme?.routes?.checkout_url || '/checkout';
        } catch (err) {
          console.error(err);
        }
      });
    });
  }

  /* ─── Quantity ─── */
  function bindQuantity() {
    const form = document.querySelector('[data-ppdp-product-form]');
    if (!form) return;

    const mainQty = document.querySelector('[data-ppdp-qty]');
    const mainInput = mainQty?.querySelector('input[name="quantity"]');
    let adding = false;

    /** @param {number} value */
    const syncInput = (value) => {
      if (mainInput) mainInput.value = String(Math.max(1, value));
    };

    /** @param {number} quantity @param {boolean} [resetAfter] */
    const autoAdd = async (quantity, resetAfter = false) => {
      const atc = form.querySelector('[data-ppdp-atc]');
      if (!atc || atc.disabled || adding) return false;

      adding = true;
      mainQty?.classList.add('is-adding');

      try {
        await addProductToCart(form, quantity, { silentAdd: true });
        if (resetAfter) syncInput(1);
        return true;
      } catch (err) {
        console.error(err);
        return false;
      } finally {
        adding = false;
        mainQty?.classList.remove('is-adding');
      }
    };

    if (!mainQty) return;
    const input = mainQty.querySelector('input');
    if (!input) return;

    mainQty.querySelector('[data-ppdp-qty-minus]')?.addEventListener('click', () => {
      syncInput(Number(mainInput?.value || 1) - 1);
    });

    mainQty.querySelector('[data-ppdp-qty-plus]')?.addEventListener('click', async () => {
      syncInput(Number(mainInput?.value || 1) + 1);
      await autoAdd(1);
    });

    input.addEventListener('change', async () => {
      const qty = Math.max(1, Number(input.value) || 1);
      syncInput(qty);
      const added = await autoAdd(qty, true);
      if (!added) syncInput(qty);
    });
  }

  /* ─── Carousel ─── */
  class PpdpCarousel {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.track = root.querySelector('[data-ppdp-track]');
      root.querySelector('[data-ppdp-prev]')?.addEventListener('click', () => this.scroll(-1));
      root.querySelector('[data-ppdp-next]')?.addEventListener('click', () => this.scroll(1));
    }

    scroll(dir) {
      if (!this.track) return;
      const slide = this.track.querySelector('.ppdp-slide, .ppdp-card, .ppdp-ba-card, .ppdp-ingredient');
      if (!slide) return;
      const gap = parseInt(getComputedStyle(this.track).gap) || 16;
      this.track.scrollLeft += dir * (slide.offsetWidth + gap);
    }
  }

  /* ─── Accordion (single open) ─── */
  class PpdpAccordion {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.single = root.dataset.single !== 'false';
      this.items = Array.from(root.querySelectorAll(':scope > [data-ppdp-accordion-item]'));
      this.items.forEach((item) => {
        const trigger = item.querySelector(':scope > [data-ppdp-accordion-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', (event) => {
          event.stopPropagation();
          this.toggle(trigger);
        });
      });
    }

    /** @param {HTMLElement} trigger */
    toggle(trigger) {
      const item = trigger.closest('[data-ppdp-accordion-item]');
      if (!item || item.parentElement !== this.root) return;
      const isOpen = item.classList.contains('is-open');

      if (this.single) {
        this.items.forEach((el) => {
          if (el !== item) {
            el.classList.remove('is-open');
            const siblingTrigger = el.querySelector(':scope > [data-ppdp-accordion-trigger]');
            if (siblingTrigger) siblingTrigger.setAttribute('aria-expanded', 'false');
          }
        });
      }

      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    }
  }

  /* ─── Premium cart drawer + bar ─── */
  class PpdpCartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-ppdp-cart-drawer]');
      this.bar = document.querySelector('[data-ppdp-cart-bar]');
      if (!this.drawer) return;

      /** @type {number | null} */
      this.closeTimer = null;
      /** @type {number | null} */
      this.barHideTimer = null;

      this.bindClose();
      this.checkoutBtn = this.drawer.querySelector('[data-ppdp-drawer-checkout]');
      this.minimumNotice = this.drawer.querySelector('[data-ppdp-drawer-minimum-notice]');
      this.lineUpdating = false;

      this.drawer.querySelector('[data-ppdp-drawer-items]')?.addEventListener('click', (event) => {
        this.handleLineItemClick(event);
      });

      this.checkoutBtn?.addEventListener('click', async (event) => {
        if (this.checkoutBtn?.classList.contains('is-disabled')) {
          event.preventDefault();
          return;
        }

        try {
          const cart = await this.fetchCart();
          applyCartMinimum(cart);

          if (!isCartEligible(cart)) {
            event.preventDefault();
            return;
          }

          const checkoutUrl = window.Theme?.routes?.checkout_url || '/checkout';
          if (
            this.checkoutBtn instanceof HTMLAnchorElement &&
            (!this.checkoutBtn.href || this.checkoutBtn.getAttribute('href') == null)
          ) {
            event.preventDefault();
            window.location.assign(checkoutUrl);
          }
        } catch {
          /* allow default navigation when cart fetch fails */
        }
      });

      this.bar?.querySelector('[data-ppdp-cart-bar-open]')?.addEventListener('click', () => {
        if (this.drawer.classList.contains('is-open')) {
          this.close();
          return;
        }
        this.openCheckout();
      });

      document.addEventListener(
        StandardEvents.cartLinesUpdate,
        (event) => {
          if (!document.body.classList.contains('template-product') || event.action !== 'add') return;
          if (useThemeCartDrawerOnMobile()) return;
          this.closeThemeDrawer();
          event.promise?.finally(() => this.closeThemeDrawer());
        },
        true
      );

      document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        event.promise
          ?.then((result) => {
            const detail = result?.detail || {};
            if (detail.didError) return;

            const count = result?.cart?.totalQuantity ?? detail.cartItemCount ?? 0;
            if (count > 0) {
              this.updateBar(count);
            }

            if (this.drawer.classList.contains('is-open') && !useThemeCartDrawerOnMobile()) {
              this.fetchCart()
                .then((cart) => this.populateFromCart(cart))
                .catch(() => {});
            }

            if (!document.body.classList.contains('template-product')) return;

            if (useThemeCartDrawerOnMobile()) {
              this.close();
              if (event.action === 'add' && count > 0) {
                requestAnimationFrame(() => openThemeCartDrawer());
              }
              if (detail.source === 'premium-pdp') {
                const image = document.querySelector('[data-ppdp-main] img')?.getAttribute('src');
                if (image) this.updateBarImage(image);
              }
              return;
            }

            this.closeThemeDrawer();
            if (detail.silentAdd) {
              requestAnimationFrame(() => this.closeThemeDrawer());
            }
            if (detail.source === 'premium-pdp') {
              const image = document.querySelector('[data-ppdp-main] img')?.getAttribute('src');
              if (image) this.updateBarImage(image);
            }
          })
          .catch(() => {});
      });

      this.initBar();
    }

    async initBar() {
      try {
        const cart = await this.fetchCart();
        if (cart.item_count > 0) {
          this.updateBar(cart.item_count);
          const last = cart.items?.[cart.items.length - 1];
          if (last?.image) this.updateBarImage(last.image);
          this.showBar();
        }
      } catch {
        /* ignore */
      }
    }

    /** @param {string} src */
    updateBarImage(src) {
      const img = this.bar?.querySelector('.ppdp-cart-bar__img');
      if (!img || !src) return;
      img.src = src;
      img.hidden = false;
    }

    /** @param {object} cart */
    updateCheckoutEligibility(cart) {
      applyCartMinimum(cart);
    }

    closeThemeDrawer() {
      document.querySelector('theme-drawer#cart-drawer')?.close?.();
    }

    async fetchCart() {
      return fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`).then((r) => r.json());
    }

    /** @param {MouseEvent} event */
    async handleLineItemClick(event) {
      if (this.lineUpdating) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const row = target.closest('[data-ppdp-line-key]');
      if (!row || !(row instanceof HTMLElement)) return;

      const lineKey = row.dataset.ppdpLineKey;
      const currentQty = Number(row.dataset.ppdpLineQty) || 1;
      let nextQty = currentQty;

      if (target.closest('[data-ppdp-line-minus]')) {
        nextQty = Math.max(0, currentQty - 1);
      } else if (target.closest('[data-ppdp-line-plus]')) {
        nextQty = currentQty + 1;
      } else if (target.closest('[data-ppdp-line-remove]')) {
        nextQty = 0;
      } else {
        return;
      }

      event.preventDefault();
      await this.updateLineItem(lineKey, nextQty);
    }

    /** @param {string} lineKey @param {number} quantity */
    async updateLineItem(lineKey, quantity) {
      if (!lineKey || this.lineUpdating) return;

      this.lineUpdating = true;
      this.drawer.classList.add('is-updating');

      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: quantity > 0 ? 'update' : 'remove',
          context: 'ppdp-cart-drawer',
          lines: [{ id: lineKey, quantity }],
          promise: deferred.promise,
        })
      );

      try {
        const changeUrl = `${window.Theme?.routes?.cart_change_url || '/cart/change'}.js`;
        const res = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity }),
        });
        const data = await res.json();

        if (data.message || data.description) {
          throw new Error(data.message || data.description);
        }

        const cart = await this.fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            didError: false,
            source: 'premium-pdp',
            silentAdd: true,
            cartItemCount: cart.item_count,
            cartTotal: cart.total_price,
          },
        });

        if (cart.item_count > 0) {
          this.populateFromCart(cart);
          this.updateBar(cart.item_count);
          const last = cart.items?.[cart.items.length - 1];
          if (last?.image) this.updateBarImage(last.image);
        } else {
          this.updateBar(0);
          this.close();
        }
      } catch (err) {
        deferred.reject(err);
        console.error(err);
      } finally {
        this.lineUpdating = false;
        this.drawer.classList.remove('is-updating');
      }
    }

    /** @param {object} cart */
    renderCartItems(cart) {
      const container = this.drawer.querySelector('[data-ppdp-drawer-items]');
      if (!container) return;

      if (!cart.items?.length) {
        container.innerHTML = '<p class="ppdp-cart-drawer__empty">Your cart is empty</p>';
        return;
      }

      container.innerHTML = cart.items
        .map((item) => {
          const variant =
            item.variant_title && item.variant_title !== 'Default Title'
              ? `<p class="ppdp-cart-drawer__item-variant">${escapeHtml(item.variant_title)}</p>`
              : '';
          const imgSrc = item.image || '';
          const img = imgSrc
            ? `<img class="ppdp-cart-drawer__item-img" src="${escapeHtml(imgSrc)}" alt="" loading="lazy" width="72" height="72">`
            : '<div class="ppdp-cart-drawer__item-img ppdp-cart-drawer__item-img--placeholder"></div>';

          return `
            <article class="ppdp-cart-drawer__item" data-ppdp-line-key="${escapeHtml(item.key)}" data-ppdp-line-qty="${item.quantity}">
              ${img}
              <div class="ppdp-cart-drawer__item-info">
                <p class="ppdp-cart-drawer__item-title">${escapeHtml(item.product_title)}</p>
                ${variant}
                <div class="ppdp-cart-drawer__item-actions">
                  <div class="ppdp-cart-drawer__qty" aria-label="Quantity">
                    <button type="button" class="ppdp-cart-drawer__qty-btn" data-ppdp-line-minus aria-label="Decrease quantity">−</button>
                    <span class="ppdp-cart-drawer__qty-value">${item.quantity}</span>
                    <button type="button" class="ppdp-cart-drawer__qty-btn" data-ppdp-line-plus aria-label="Increase quantity">+</button>
                  </div>
                  <button type="button" class="ppdp-cart-drawer__remove" data-ppdp-line-remove>Remove</button>
                </div>
                <p class="ppdp-cart-drawer__item-meta">${formatMoney(item.final_line_price)}</p>
              </div>
            </article>
          `;
        })
        .join('');
    }

    /** @param {object} cart */
    renderCartTotals(cart) {
      const subtotalEl = this.drawer.querySelector('[data-ppdp-drawer-subtotal]');
      const discountRow = this.drawer.querySelector('[data-ppdp-drawer-discount-row]');
      const discountEl = this.drawer.querySelector('[data-ppdp-drawer-discount]');
      const totalEl = this.drawer.querySelector('[data-ppdp-drawer-cart-total]');

      const subtotal = Number(cart.items_subtotal_price) || 0;
      const discount = Number(cart.total_discount) || 0;
      const total = Number(cart.total_price) || 0;
      const count = Number(cart.item_count) || 0;

      if (subtotalEl) subtotalEl.textContent = formatMoney(subtotal);

      if (discountRow && discountEl) {
        if (discount > 0) {
          discountRow.hidden = false;
          discountEl.textContent = `-${formatMoney(discount)}`;
        } else {
          discountRow.hidden = true;
          discountEl.textContent = '';
        }
      }

      if (totalEl) totalEl.textContent = formatMoney(total);

      this.drawer.querySelectorAll('[data-ppdp-drawer-cart-count]').forEach((el) => {
        el.textContent = String(count);
      });

      const note = this.drawer.querySelector('.ppdp-cart-drawer__summary-note');
      if (note) {
        note.innerHTML = `<span data-ppdp-drawer-cart-count>${count}</span> ${itemLabel(count)} in your cart`;
      }
    }

    /** @param {object} cart */
    populateFromCart(cart) {
      const titleEl = this.drawer.querySelector('#ppdp-cart-drawer-title');
      const subtitleEl = this.drawer.querySelector('[data-ppdp-drawer-subtitle]');
      const count = Number(cart.item_count) || 0;
      const total = Number(cart.total_price) || 0;

      if (titleEl) titleEl.textContent = 'Your cart';
      if (subtitleEl) {
        subtitleEl.textContent =
          count > 0
            ? `${count} ${count === 1 ? 'item' : 'items'} · ${formatMoney(total)} total`
            : 'Review your cart before proceeding';
      }

      this.renderCartItems(cart);
      this.renderCartTotals(cart);
      this.updateCheckoutEligibility(cart);
    }

    async openCheckout() {
      if (useThemeCartDrawerOnMobile()) {
        try {
          const cart = await this.fetchCart();
          if (!cart.item_count) return;
          this.close();
          openThemeCartDrawer();
        } catch (err) {
          console.error(err);
        }
        return;
      }

      try {
        const cart = await this.fetchCart();
        if (!cart.item_count) return;
        this.populateFromCart(cart);
        this.closeThemeDrawer();
        this.open();
      } catch (err) {
        console.error(err);
      }
    }

    /** @param {number} count */
    updateBar(count) {
      const safeCount = Math.max(0, count);
      this.bar?.querySelectorAll('[data-ppdp-cart-bar-count]').forEach((el) => {
        el.textContent = String(safeCount);
      });
      const label = this.bar?.querySelector('[data-ppdp-cart-bar-label]');
      if (label) label.textContent = barItemLabel(safeCount);

      if (safeCount > 0) this.showBar();
      else this.hideBar();
    }

    clearCloseTimer() {
      if (this.closeTimer != null) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    clearBarHideTimer() {
      if (this.barHideTimer != null) {
        clearTimeout(this.barHideTimer);
        this.barHideTimer = null;
      }
    }

    showBar() {
      if (!this.bar) return;
      this.clearBarHideTimer();
      this.bar.hidden = false;
      requestAnimationFrame(() => this.bar?.classList.add('is-visible'));
    }

    hideBar() {
      if (!this.bar) return;
      this.bar.classList.remove('is-visible');
      this.clearBarHideTimer();
      this.barHideTimer = window.setTimeout(() => {
        if (!this.bar?.classList.contains('is-visible')) {
          this.bar.hidden = true;
        }
        this.barHideTimer = null;
      }, 320);
    }

    open() {
      this.clearCloseTimer();
      this.drawer.hidden = false;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('ppdp-cart-drawer-open');
      void this.drawer.offsetHeight;
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        document.body.classList.add('ppdp-cart-drawer-open');
      });
    }

    close() {
      if (!this.drawer.classList.contains('is-open') && this.drawer.hidden) return;

      this.clearCloseTimer();
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('ppdp-cart-drawer-open');
      this.closeTimer = window.setTimeout(() => {
        if (!this.drawer.classList.contains('is-open')) {
          this.drawer.hidden = true;
        }
        this.closeTimer = null;
      }, 350);
    }

    bindClose() {
      this.drawer.querySelectorAll('[data-ppdp-cart-drawer-close]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      });

      if (!this._escapeBound) {
        this._escapeBound = true;
        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
            this.close();
          }
        });
      }
    }
  }

  /* ─── Recently viewed ─── */
  class PpdpRecentlyViewed {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.handle = root.dataset.productHandle;
      this.init();
    }

    init() {
      if (!this.handle) return;
      const key = 'ppdp_viewed_handles';
      let handles = JSON.parse(localStorage.getItem(key) || '[]');
      handles = handles.filter((h) => h !== this.handle);
      handles.unshift(this.handle);
      handles = handles.slice(0, 12);
      localStorage.setItem(key, JSON.stringify(handles));

      const show = handles.filter((h) => h !== this.handle).slice(0, 8);
      if (!show.length) return;
      this.root.hidden = false;
      this.render(show);
    }

    /** @param {string[]} handles */
    async render(handles) {
      const track = this.root.querySelector('[data-ppdp-track]');
      if (!track) return;

      const products = await Promise.all(
        handles.map(async (handle) => {
          try {
            const res = await fetch(`/products/${handle}.js`);
            if (!res.ok) return null;
            return res.json();
          } catch {
            return null;
          }
        })
      );

      track.innerHTML = '';
      products.filter(Boolean).forEach((p) => track.appendChild(this.buildSlide(p)));
    }

    /** @param {object} product */
    buildSlide(product) {
      const v = product.variants[0];
      const slide = document.createElement('div');
      slide.className = 'ppdp-slide';
      slide.innerHTML = `
        <article class="ppdp-card">
          <a href="/products/${product.handle}" class="ppdp-card__media">
            <img src="${product.featured_image || product.images[0]}" alt="" loading="lazy" width="400" height="400">
          </a>
          <div class="ppdp-card__body">
            <h3 class="ppdp-card__title"><a href="/products/${product.handle}">${product.title}</a></h3>
            <p class="ppdp-card__price">${formatMoney(v.price)}</p>
            <button type="button" class="ppdp-card__atc" data-ppdp-quick-add data-variant-id="${v.id}">Add to Cart</button>
          </div>
        </article>`;
      return slide;
    }
  }

  /* ─── Recommendations ─── */
  class PpdpRecommendations {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.productId = root.dataset.productId;
      this.intent = root.dataset.intent || 'related';
      if (root.dataset.ppdpRecommendations === '') this.load();
    }

    async load() {
      const track = this.root.querySelector('[data-ppdp-track]');
      if (!track || !this.productId) return;

      const existing = track.querySelectorAll('.ppdp-slide');
      if (existing.length > 1) return;

      try {
        const url = `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${this.productId}&limit=8&intent=${this.intent}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        const products = data.products || [];

        if (!products.length) return;

        track.innerHTML = '';
        products.forEach((p) => track.appendChild(this.buildSlide(p)));
      } catch {
        /* keep SSR fallback cards */
      }
    }

    /** @param {object} product */
    buildSlide(product) {
      const v = product.variants?.[0] || { id: '', price: 0 };
      const slide = document.createElement('div');
      slide.className = 'ppdp-slide';
      const img = product.featured_image || product.images?.[0] || '';
      slide.innerHTML = `
        <article class="ppdp-card">
          <a href="/products/${product.handle}" class="ppdp-card__media">
            ${img ? `<img src="${img}" alt="" loading="lazy" width="400" height="400">` : ''}
          </a>
          <div class="ppdp-card__body">
            <h3 class="ppdp-card__title"><a href="/products/${product.handle}">${product.title}</a></h3>
            <p class="ppdp-card__price">${formatMoney(v.price)}</p>
            <button type="button" class="ppdp-card__atc" data-ppdp-quick-add data-variant-id="${v.id}">Add to Cart</button>
          </div>
        </article>`;
      return slide;
    }
  }

  /* ─── Wishlist (localStorage) ─── */
  function bindWishlist() {
    document.querySelectorAll('[data-ppdp-wishlist]').forEach((btn) => {
      const id = btn.dataset.productId;
      const key = 'ppdp_wishlist';
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      if (list.includes(id)) btn.classList.add('is-active');

      btn.addEventListener('click', () => {
        let list = JSON.parse(localStorage.getItem(key) || '[]');
        if (list.includes(id)) {
          list = list.filter((i) => i !== id);
          btn.classList.remove('is-active');
        } else {
          list.push(id);
          btn.classList.add('is-active');
        }
        localStorage.setItem(key, JSON.stringify(list));
      });
    });
  }

  /* ─── Share ─── */
  function bindShare() {
    document.querySelectorAll('[data-ppdp-share]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const data = { title: document.title, url: window.location.href };
        if (navigator.share) {
          await navigator.share(data);
        } else {
          await navigator.clipboard.writeText(data.url);
          btn.textContent = 'Link copied!';
          setTimeout(() => { btn.textContent = 'Share'; }, 2000);
        }
      });
    });
  }

  /* ─── Quick add ─── */
  function bindQuickAdd() {
    document.addEventListener('click', async (event) => {
      const btn = event.target.closest('[data-ppdp-quick-add]');
      if (!btn) return;
      event.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      btn.disabled = true;

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        await addProductToCart(formData, undefined, { silentAdd: true });
        btn.textContent = 'Added!';
        setTimeout(() => {
          btn.textContent = 'Add to Cart';
        }, 1500);
      } catch (err) {
        console.error(err);
      } finally {
        btn.disabled = false;
      }
    });
  }

  /* ─── Marketplace links (variant-aware) ─── */
  class PpdpMarketplaceLinks {
    constructor() {
      this.root = document.querySelector('[data-ppdp-marketplaces]');
      this.list = this.root?.querySelector('[data-ppdp-marketplaces-list]');
      this.dataEl = document.querySelector('[data-ppdp-marketplaces-data]');
      if (!this.root || !this.list || !this.dataEl) return;

      this.links = this.parseLinks();
      if (!this.links.length) return;

      document.addEventListener('ppdp:variant-change', (event) => {
        this.render(event.detail?.variant);
      });

      const variantInput = document.querySelector('[data-ppdp-product-form] [name="id"]');
      const initialVariant = this.findVariantById(variantInput?.value);
      this.render(initialVariant);
    }

    parseLinks() {
      try {
        const parsed = JSON.parse(this.dataEl.textContent);
        return Array.isArray(parsed) ? parsed.filter((link) => link?.url) : [];
      } catch {
        return [];
      }
    }

    findVariantById(id) {
      if (!id) return null;
      try {
        const dataEl = document.querySelector('[data-ppdp-variants]');
        const data = dataEl ? JSON.parse(dataEl.textContent) : null;
        return data?.variants?.find((variant) => String(variant.id) === String(id)) || null;
      } catch {
        return null;
      }
    }

    normalizeVariantTitle(title) {
      const value = String(title || '').trim();
      return value === 'Default Title' ? '' : value;
    }

    linkMatchesVariant(link, variantTitle) {
      const linkVariant = this.normalizeVariantTitle(link.variant);
      if (!linkVariant) return true;
      const selected = this.normalizeVariantTitle(variantTitle);
      return linkVariant.toLowerCase() === selected.toLowerCase();
    }

    buildButtonLabel(link, visibleLinks) {
      const suffix = String(link.label || link.variant || '').trim();
      const samePlatform = visibleLinks.filter((item) => item.platformKey === link.platformKey);
      if (samePlatform.length > 1 && suffix) {
        return `${link.name} · ${suffix}`;
      }
      return link.name;
    }

    /** @param {{ title?: string } | null | undefined} variant */
    render(variant) {
      const variantTitle = variant?.title || '';
      const visible = this.links.filter((link) => this.linkMatchesVariant(link, variantTitle));

      this.list.innerHTML = '';

      if (!visible.length) {
        this.root.hidden = true;
        return;
      }

      visible.forEach((link) => {
        const anchor = document.createElement('a');
        anchor.href = link.url;
        anchor.className = `ppdp-marketplace-btn ppdp-marketplace-btn--${link.platformKey || 'shop'}`;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        anchor.textContent = this.buildButtonLabel(link, visible);
        this.list.appendChild(anchor);
      });

      this.root.hidden = false;
    }
  }

  /* ─── Init ─── */
  function init() {
    document.querySelectorAll('[data-ppdp-gallery]').forEach((el) => new PpdpGallery(el));
    document.querySelectorAll('[data-ppdp-variants-root]').forEach((el) => new PpdpVariants(el));
    document.querySelectorAll('[data-ppdp-product-form]').forEach((f) => new PpdpForm(f));
    document.querySelectorAll('[data-ppdp-carousel]').forEach((el) => new PpdpCarousel(el));
    document.querySelectorAll('[data-ppdp-accordion]').forEach((el) => new PpdpAccordion(el));
    document.querySelectorAll('[data-ppdp-recently-viewed]').forEach((el) => new PpdpRecentlyViewed(el));
    document.querySelectorAll('[data-ppdp-recommendations]').forEach((el) => new PpdpRecommendations(el));

    new PpdpCartDrawer();
    new PpdpMarketplaceLinks();
    bindBuyNow();
    bindQuantity();
    bindWishlist();
    bindShare();
    bindQuickAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
